/**
 * Direct-data-import field-type derivation.
 *
 * The import UI offers TWO text options — "Short Text" and "Long Text" — and
 * BOTH are backed by `comp: "LexicalTextEditor"`. Short Text is a Lexical
 * block carrying the marker prop `shortText: true` (the BE detection emits it,
 * see okf-be directImportFieldTypeOptions.js); LexicalTextEditor derives its
 * plain-input rendering from that flag. Because two options share a comp, the
 * UI selects options by a stable `key` ("shortText" / "longText" / "number" /
 * ...), never by comp. This module is the single place that maps an outline
 * item (or an existingFields entry) to its option key.
 */

// Deprecated rich-text editors that may still exist as fields in older
// templates. They display as "Long Text" and stay locked (not editable).
export const LEGACY_RICH_TEXT_COMPS = ["KPRichInput", "RichTextEditor"];

// CANONICAL short marker — keep in sync with the BE preset
// (okf-be directImportFieldTypeOptions.js getShortTextExplicitBlockConfig) and
// the listing projection (pageTpls/PublishedListing/config/fixedGetDataConfig).
// Only the Short Text preset ever writes this prop, so the test is unambiguous
// on its own — unlike `variant: "simple"`, which merely means "render as plain
// text" and is true of long-form fields too.
export const isShortTextField = (props) => props?.shortText === true;

/**
 * Maps an outline item OR an existingFields entry to its field-type option
 * key. Outline items carry props under `explicitBlockConfig.props`;
 * existingFields entries carry a minimal `props` subset directly (BE
 * buildExistingFieldsFromTpl).
 */
export const deriveFieldTypeKey = (item, fieldTypeOptions = []) => {
  if (!item) return undefined;
  if (item.dummyCompNameValue === "title") return "title";

  const props = item.explicitBlockConfig?.props ?? item.props;

  // Legacy plain-text fields (pre-Short/Long datasets, and the title comp)
  // display as Short Text but keep their TextInput comp — they are never
  // auto-converted.
  if (item.comp === "TextInput") return "shortText";

  if (item.comp === "LexicalTextEditor") {
    return isShortTextField(props) ? "shortText" : "longText";
  }

  if (LEGACY_RICH_TEXT_COMPS.includes(item.comp)) return "longText";

  // Everything else resolves by comp; fall back to the comp itself so
  // non-import types (e.g. TitleInput on reimport) simply find no option and
  // stay locked, as today.
  return fieldTypeOptions.find((o) => o.comp === item.comp)?.key ?? item.comp;
};

// Removes the Short Text preset keys (shortText / annotations / chunking) from
// a column's explicitBlockConfig.props, preserving all other keys (label, title
// keys, adapter-preset keys) and pruning the objects it empties. Used whenever
// a column stops being Short Text: a type change, the title designation, or
// being mapped onto an existing field. Mutates in place. Keep this list in step
// with the BE preset factory (okf-be directImportFieldTypeOptions.js
// getShortTextExplicitBlockConfig) — a key left behind survives onto the stored
// block: `chunking.disable` stranded on a Long Text column would silently keep
// it out of chunking/embedding.
export const stripShortTextPresetProps = (col) => {
  const props = col.explicitBlockConfig?.props;
  if (!props) return;
  delete props.shortText;
  delete props.annotations;
  delete props.chunking;
  if (Object.keys(props).length === 0) delete col.explicitBlockConfig.props;
  if (
    col.explicitBlockConfig &&
    Object.keys(col.explicitBlockConfig).length === 0
  ) {
    delete col.explicitBlockConfig;
  }
};

// "Include in AI Analysis" state of an item: ON unless the registry
// kill-switch (props.annotations.disable) is set. The toggle writes that flag
// directly on the outline item's explicitBlockConfig — there is no separate
// FE-only AI flag anymore. Marker-based derivation above is deliberately
// independent of this: a Long Text field with AI off still derives longText.
export const isAiAnalysisOnForOutlineItem = (item) =>
  (item?.explicitBlockConfig?.props ?? item?.props)?.annotations?.disable !==
  true;
